import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { CONTEXTS, CONTROLLERS } from 'figshelf-modules';

const normalizeString = (value) => String(value || '').trim().toLowerCase();

export const CountrySelect = ({
  label = 'Country',
  value,
  onChange,
  onSelect,
  disabled,
  required,
  textFieldProps,
  autocompleteProps,
}) => {
  const api = useContext(CONTEXTS.APIContext);
  const locationsController = useMemo(() => new CONTROLLERS.LocationsController(api), [api]);

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const didInitSelectRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    Promise.resolve()
      .then(() => locationsController.getCountries())
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.data;
        setCountries(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load countries:', err);
        setCountries([]);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [locationsController]);

  const selectedOption = useMemo(() => {
    const v = normalizeString(value);
    if (!v) return null;
    return (
      countries.find((c) => normalizeString(c?.name) === v) ||
      countries.find((c) => normalizeString(c?.code) === v) ||
      null
    );
  }, [countries, value]);

  useEffect(() => {
    if (!onSelect) return;
    // Call onSelect whenever the `value` resolves to a known country option.
    // This handles cases where the user types or pastes the country name instead
    // of explicitly selecting the option from the Autocomplete list.
    if (!countries.length) {
      onSelect(null);
      return;
    }
    onSelect(selectedOption || null);
  }, [countries.length, onSelect, selectedOption]);

  return (
    <Autocomplete
      options={countries}
      value={selectedOption}
      loading={loading}
      disabled={disabled}
      autoHighlight
      getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
      isOptionEqualToValue={(option, val) => option?.code === val?.code}
      onChange={(_, newValue) => {
        const option = newValue || null;
        if (!option) {
          onChange?.('');
          onSelect?.(null);
          return;
        }
        onChange?.(option.name);
        onSelect?.(option);
      }}
      renderInput={(params) => {
        const { InputProps: textFieldInputProps, ...restTextFieldProps } = textFieldProps || {};
        const mergedEndAdornment = (
          <>
            {loading ? <CircularProgress color="inherit" size={18} /> : null}
            {textFieldInputProps?.endAdornment}
            {params.InputProps.endAdornment}
          </>
        );

        return (
          <TextField
            {...params}
            label={label}
            required={required}
            disabled={disabled}
            InputProps={{
              ...params.InputProps,
              ...textFieldInputProps,
              endAdornment: mergedEndAdornment,
            }}
            {...restTextFieldProps}
          />
        );
      }}
      {...autocompleteProps}
    />
  );
};

export const CitySelect = ({
  label = 'City',
  value,
  onChange,
  countryCode,
  disabled,
  required,
  textFieldProps,
  autocompleteProps,
}) => {
  const api = useContext(CONTEXTS.APIContext);
  const locationsController = useMemo(() => new CONTROLLERS.LocationsController(api), [api]);

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState(value || '');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const q = String(inputValue || '').trim();
    if (!q) {
      // If no input but a country was selected, prefetch some common cities for that country
      if (countryCode) {
        let cancelled = false;
        setLoading(true);
        Promise.resolve()
          .then(() =>
            locationsController.searchCities({
              q: 'a', // lightweight query to return many entries from the country
              country: countryCode,
              limit: 50,
            }),
          )
          .then((res) => {
              if (cancelled) return;
              const list = Array.isArray(res) ? res : res?.data;
              const raw = Array.isArray(list) ? list : [];

              // Normalize and deduplicate by name+admin1+countryCode+lat+lon to avoid duplicate keys
              const map = new Map();
              raw.forEach((r) => {
                const name = (r.name || '').trim();
                const admin1 = (r.admin1 || '').trim();
                const countryC = (r.countryCode || '').trim().toUpperCase();
                const lat = r.latitude || '';
                const lon = r.longitude || '';
                const id = `${name}||${admin1}||${countryC}||${lat}||${lon}`;
                if (!map.has(id)) {
                  map.set(id, {
                    id,
                    name,
                    admin1: admin1 || '',
                    admin2: r.admin2 || '',
                    countryCode: countryC,
                    latitude: r.latitude,
                    longitude: r.longitude,
                  });
                }
              });

              setOptions(Array.from(map.values()));
            })
          .catch((err) => {
            if (cancelled) return;
            console.error('Failed to preload cities:', err);
            setOptions([]);
          })
          .finally(() => {
            if (cancelled) return;
            setLoading(false);
          });

        return () => { cancelled = true; };
      }

      setOptions([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      let cancelled = false;
      setLoading(true);

      Promise.resolve()
        .then(() =>
          locationsController.searchCities({
            q,
            country: countryCode || undefined,
            limit: 10,
          }),
        )
        .then((res) => {
          if (cancelled) return;
          const list = Array.isArray(res) ? res : res?.data;
          const raw = Array.isArray(list) ? list : [];

          // Normalize and dedupe like prefetch
          const map = new Map();
          raw.forEach((r) => {
            const name = (r.name || '').trim();
            const admin1 = (r.admin1 || '').trim();
            const countryC = (r.countryCode || '').trim().toUpperCase();
            const lat = r.latitude || '';
            const lon = r.longitude || '';
            const id = `${name}||${admin1}||${countryC}||${lat}||${lon}`;
            if (!map.has(id)) {
              map.set(id, {
                id,
                name,
                admin1: admin1 || '',
                admin2: r.admin2 || '',
                countryCode: countryC,
                latitude: r.latitude,
                longitude: r.longitude,
              });
            }
          });

          setOptions(Array.from(map.values()));
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('Failed to load cities:', err);
          setOptions([]);
        })
        .finally(() => {
          if (cancelled) return;
          setLoading(false);
        });

      return () => {
        cancelled = true;
      };
    }, 250);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [countryCode, inputValue, locationsController]);

  return (
    <Autocomplete
      openOnFocus
      freeSolo
      options={options}
      value={value || ''}
      inputValue={inputValue}
      loading={loading}
      disabled={disabled}
      autoHighlight
      filterOptions={(x) => x}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        if (!option) return '';
        const name = option.name || '';
        const admin1 = option.admin1 ? `, ${option.admin1}` : '';
        return `${name}${admin1}`;
      }}
      isOptionEqualToValue={(option, val) => {
        if (typeof option === 'string' || typeof val === 'string') return option === val;
        return option?.id === val?.id;
      }}
      renderOption={(props, option) => {
        const label = typeof option === 'string' ? option : `${option.name}${option.admin1 ? `, ${option.admin1}` : ''}`;
        return (
          <li {...props} key={option?.id ?? label}>
            {label}
          </li>
        );
      }}
      onInputChange={(_, newInput) => {
        setInputValue(newInput);
        if (!newInput) {
          onChange?.('');
        }
      }}
      onChange={(_, newValue) => {
        if (typeof newValue === 'string') {
          onChange?.(newValue);
          return;
        }
        if (!newValue) {
          onChange?.('');
          return;
        }
        // Provide both name and admin1 (state/province) when available
        if (onChange) {
          const payload = {
            name: newValue.name,
            admin1: newValue.admin1 || '',
          };
          onChange(payload);
        }
      }}
      renderInput={(params) => {
        const { InputProps: textFieldInputProps, ...restTextFieldProps } = textFieldProps || {};
        const mergedEndAdornment = (
          <>
            {loading ? <CircularProgress color="inherit" size={18} /> : null}
            {textFieldInputProps?.endAdornment}
            {params.InputProps.endAdornment}
          </>
        );

        return (
          <TextField
            {...params}
            label={label}
            required={required}
            disabled={disabled}
            InputProps={{
              ...params.InputProps,
              ...textFieldInputProps,
              endAdornment: mergedEndAdornment,
            }}
            {...restTextFieldProps}
          />
        );
      }}
      {...autocompleteProps}
    />
  );
};
